import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./markdown.html";
import { marked } from 'marked';
// This component renders REAL third-party content, not fixtures. Callers pass the `skillmd` field of a
// listing fetched from the publishing-service listings API (see ~/modules/utilities/skill_utils) — a
// skill's SKILL.md, ingested upstream from the forcedotcom/sf-skills repo. It crosses a service
// boundary before it reaches us and nothing in this repo controls its bytes, so the markdown that
// renderedCallback() hands to innerHTML has to be treated as hostile.
//
// Sanitization is therefore an ALLOWLIST enforced structurally: marked's output is parsed into a
// detached document with DOMParser, every element and every attribute is checked against the tables
// below, and only what survives is serialized back out. The previous implementation was an allowlist
// too, but enforced by regex: it deleted every tag whose *name* failed a negative lookahead against
// the allowed set (`<(/?(?!a|b|em|...)\b[^>]*?)>`). Matching names is the limitation — the expression
// cannot see attributes at all, so `<img src=x onerror=alert(1)>`, `<a href="javascript:...">` and
// `<p onclick=...>` all passed through untouched, because `img`, `a` and `p` are allowed tags and
// only the tag name was ever examined. Regex is the wrong tool for parsing HTML; the fix is to let
// the platform's parser do it.
//
// What the default policy guarantees: no element outside ALLOWED_TAGS, no attribute outside
// ALLOWED_ATTRIBUTES, no `on*` handler, no `style`, and no `href`/`src` scheme other than
// http/https/mailto or a URL with no scheme at all. Untrusted images have an additional boundary:
// their final URL must remain under a caller-provided, commit-pinned root for the listing's own
// repository. This prevents publisher-controlled tracking pixels while preserving relative package
// images. Links may still point wherever the author chose. And because the sanitized tree is
// serialized to a string that innerHTML re-parses, there is a theoretical mutation-XSS surface;
// dropping `svg`/`math` subtrees wholesale closes the known namespace-confusion vectors, but if this
// content model widens beyond SKILL.md, reach for a maintained sanitizer (DOMPurify) rather than
// extending this.
//
// wds/formattedRichText opts into the separate trusted-CMS policy below. That policy shares the
// structural sanitizer and security checks but restores only the known WordPress color/responsive
// classes, validated literal color styles, and tel: links used by existing CMS content. Untrusted
// SKILL.md continues to use the strict default.

// Tags that may appear in the output. Same set the regex allowlist carried — markdown legitimately
// produces the pre/code/table families, hr, h1 and blockquote that plain rich text does not. GFM
// task-list `<input>` is absent on purpose: the regex stripped it before this change too, and
// re-adding it would widen the surface for no content we have seen. What differs from the old regex:
// the attribute allowlist below (the regex saw no attributes at all), and how tags are enforced
// (structural rebuild rather than marker deletion).
const ALLOWED_TAGS = new Set(['a', 'b', 'em', 'br', 'strong', 'ol', 'ul', 'li', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'mark', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'blockquote', 'img']);

// Per-tag attribute allowlist — anything absent is dropped, so this is the complete set of attributes
// that can reach the DOM:
// - `class` on pre/code only: marked labels fenced blocks `<code class="language-ts">`, which is the
//   conventional hook a future highlighter would use, so dropping it would discard the language label.
//   `class` is NOT allowed on other tags — even though shadow DOM scopes CSS, a narrower allowlist is
//   safer when there is no content reason to widen it.
// - `alt`/`title` on img: dropping `alt` would regress screen-reader output for every skill image.
// - `align` on th/td and `start` on ol: marked emits these from `|:--|` column alignment and from
//   ordered lists that don't begin at 1, so omitting them would silently change how tables and lists
//   render.
// - Table accessibility (`scope`/`abbr` on th, `headers` on td, `colspan`/`rowspan` on both): required
//   for WCAG 1.3.1 (hand-written HTML tables in SKILL.md) and to preserve the table's structural
//   meaning. All are safe: `scope` is an enum, `abbr` is plain text, `headers` is a space-separated
//   id list, and `colspan`/`rowspan` are numeric.
// - `lang`/`dir` and `aria-label` are allowed globally for WCAG 3.1.2 (Language of Parts) and
//   screen-reader support. All are safe: `lang` is a language code, `dir` is an enum, `aria-label`
//   is plain text. Links are the exception: authored aria-label could replace trustworthy visible
//   link text with unrelated speech, so anchors deliberately omit it below.
// - `id` is allowed so a `<td headers="...">` can reference its `<th id="...">`: ARIA/IDREF
//   resolution works within a shadow tree, so this is what makes the `headers` above useful. It is
//   NOT for in-page `#fragment` links — those resolve against the *document's* id map, which a
//   shadow root's ids never join, so `href="#usage"` cannot reach a heading in here no matter what.
//   (Nothing is lost by allowing it: marked emits no heading ids at all, so the only ids that can
//   appear come from hand-written HTML in a SKILL.md.) `name` on `a` is allowed as the legacy
//   spelling of the same thing and is inert for the same reason.
//   Safe on two counts: the container is selected by CLASS (`querySelector('.container')`), so no id
//   value could collide with it under any shadow mode, and native shadow keeps these ids out of the
//   document's id map, so they cannot clobber `document.<id>` or be found by `getElementById`.
// - Image sizing (`width`/`height`/`loading` on img): safe (numeric/enum), and preserve intrinsic-size
//   reservation to reduce layout shift.
// `rel` is intentionally not listed: an author-supplied `rel` is never trusted, it is synthesized
// below whenever a link opens in a new tab.

// Attributes allowed on every tag in ALLOWED_TAGS:
const GLOBAL_ATTRIBUTES = ['lang', 'dir', 'aria-label', 'id'];
const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title', 'target', 'name', ...GLOBAL_ATTRIBUTES.filter(name => name !== 'aria-label')],
  img: ['src', 'alt', 'title', 'width', 'height', 'loading', ...GLOBAL_ATTRIBUTES],
  pre: ['class', ...GLOBAL_ATTRIBUTES],
  code: ['class', ...GLOBAL_ATTRIBUTES],
  ol: ['start', ...GLOBAL_ATTRIBUTES],
  th: ['align', 'scope', 'abbr', 'colspan', 'rowspan', ...GLOBAL_ATTRIBUTES],
  td: ['align', 'headers', 'colspan', 'rowspan', ...GLOBAL_ATTRIBUTES],
  // All other tags get GLOBAL_ATTRIBUTES only:
  b: GLOBAL_ATTRIBUTES,
  em: GLOBAL_ATTRIBUTES,
  br: GLOBAL_ATTRIBUTES,
  strong: GLOBAL_ATTRIBUTES,
  ul: GLOBAL_ATTRIBUTES,
  li: GLOBAL_ATTRIBUTES,
  p: GLOBAL_ATTRIBUTES,
  h1: GLOBAL_ATTRIBUTES,
  h2: GLOBAL_ATTRIBUTES,
  h3: GLOBAL_ATTRIBUTES,
  h4: GLOBAL_ATTRIBUTES,
  h5: GLOBAL_ATTRIBUTES,
  h6: GLOBAL_ATTRIBUTES,
  mark: GLOBAL_ATTRIBUTES,
  table: GLOBAL_ATTRIBUTES,
  thead: GLOBAL_ATTRIBUTES,
  tbody: GLOBAL_ATTRIBUTES,
  tr: GLOBAL_ATTRIBUTES,
  hr: GLOBAL_ATTRIBUTES,
  blockquote: GLOBAL_ATTRIBUTES
};

// Disallowed elements are normally unwrapped — `<div align="center">text</div>` keeps `text`, matching
// what the old regex did, since it deleted tag markers rather than content. These are the exceptions:
// their children are code or markup rather than prose, so unwrapping would either leak script text
// into the page as visible copy or hand innerHTML a namespace-confusion payload. They are removed with
// their whole subtree.
const FORBIDDEN_SUBTREE_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'textarea', 'title', 'xmp', 'svg', 'math']);

// Schemes a SKILL.md link or image may use. http/https covers documentation links and repository
// images, mailto covers support contacts, and a value with no scheme at all (relative path,
// `#anchor`, `?query`) is permitted. Markdown callers rebase relative repository links and images
// before this check. Image URLs then pass a second, stricter repository-root check below.
// `javascript:` is code execution and `data:` can carry a whole HTML document, so both are rejected —
// which also rejects legitimate inline base64 images, an acceptable trade for not validating an
// uncontrolled media type.
const SAFE_URL_SCHEMES = new Set(['http', 'https', 'mailto']);
const TRUSTED_CMS_SAFE_URL_SCHEMES = new Set([...SAFE_URL_SCHEMES, 'tel']);
const TRUSTED_CMS_CLASS_PATTERNS = [/^has-[a-z0-9-]+-color$/, /^has-inline-color$/, /^mobile-break$/];
const TRUSTED_CMS_STYLE_PROPERTIES = new Set(['color', 'background-color']);
const SAFE_CMS_COLOR_VALUE = /^(?:#[0-9a-f]{3,8}|(?:rgb|rgba|hsl|hsla)\([\d\s.,%+/-]+\)|[a-z]+)$/i;

// Markdown is injected with lwc:dom="manual", so its elements cannot carry type-style attributes in
// the component template. Add the design-system type styles after sanitization: h1 aligns with the
// page-level Display 6 headings, h2 steps down to Display 7, and every deeper level is capped at the
// smallest display style. This keeps author-controlled heading levels from visually outranking the
// surrounding Overview and Categories hierarchy without recreating the design-system typography.
const HEADING_TYPE_STYLES = {
  h1: 'display-6',
  h2: 'display-7',
  h3: 'display-8',
  h4: 'display-8',
  h5: 'display-8',
  h6: 'display-8'
};

/**
 * True when `value` is a URL this component is willing to leave in an `href`/`src`.
 *
 * Scheme detection has to survive obfuscation. DOMParser has already decoded HTML entities by the
 * time we see the value (`javascript&colon;x` and `&#106;avascript:x` both arrive here as
 * `javascript:x`), and a browser ignores leading/trailing whitespace and embedded control characters
 * when it resolves a scheme, so `java\nscript:x`, `java\tscript:x` and ` JAVASCRIPT:x` are all live
 * vectors. Strip those characters and lowercase before deciding. The stripping happens on a copy used
 * only for the decision — the attribute keeps its original value — and it can only make a URL look
 * more suspicious than the browser would, never less.
 *
 * A protocol-relative `//host/path` is treated as scheme-less and therefore allowed. It inherits our
 * own page's scheme, so it cannot execute anything; it just points at a third-party origin, which is
 * the same residual exposure an explicitly allowed `https://host/path` already carries.
 */
function isSafeUrl(value, policy) {
  // no-control-regex is disabled deliberately: matching C0 control characters is the whole point of
  // this expression. The range below is NUL through space, which is exactly the set a URL parser
  // ignores when it resolves a scheme.
  // eslint-disable-next-line no-control-regex
  const normalized = value.replace(/[\u0000-\u0020]/g, '').toLowerCase();
  const colonIndex = normalized.indexOf(':');
  if (colonIndex === -1) {
    // No scheme: a relative path, `#anchor` or `?query`; repository callers rebase paths first.
    return normalized !== '';
  }

  // A colon that follows the first `/`, `?` or `#` belongs to the path or query rather than to a
  // scheme (e.g. `./docs/a:b`), so the URL is still relative.
  const delimiterIndex = normalized.search(/[/?#]/);
  if (delimiterIndex !== -1 && delimiterIndex < colonIndex) {
    return true;
  }
  const safeSchemes = policy === 'trustedCms' ? TRUSTED_CMS_SAFE_URL_SCHEMES : SAFE_URL_SCHEMES;
  return safeSchemes.has(normalized.slice(0, colonIndex));
}

/**
 * Copies the attributes the allowlist permits from `source` onto the freshly built `target`.
 *
 * Attributes are copied onto a new element rather than removed from the parsed one because
 * removeAttribute() is not reliably reflected in serialization: `is` in particular survives
 * removeAttribute() in jsdom's serializer (hasAttribute() reports false, yet the attribute is still
 * written out). Building up from nothing means an attribute has to be explicitly allowed to exist at
 * all, so a quirk in any one parser or serializer cannot silently reintroduce one.
 */

function sanitizeTrustedCmsClasses(value, tag) {
  return value.split(/\s+/).filter(Boolean).filter(className => TRUSTED_CMS_CLASS_PATTERNS.some(pattern => pattern.test(className)) || (tag === 'pre' || tag === 'code') && /^language-[a-z0-9_-]+$/i.test(className)).join(' ');
}
function sanitizeTrustedCmsStyle(value) {
  return value.split(';').map(declaration => declaration.trim()).filter(Boolean).map(declaration => {
    const colonIndex = declaration.indexOf(':');
    if (colonIndex < 1) {
      return '';
    }
    const property = declaration.slice(0, colonIndex).trim().toLowerCase();
    const propertyValue = declaration.slice(colonIndex + 1).trim();
    if (!TRUSTED_CMS_STYLE_PROPERTIES.has(property) || !SAFE_CMS_COLOR_VALUE.test(propertyValue)) {
      return '';
    }
    return `${property}:${propertyValue}`;
  }).filter(Boolean).join(';');
}
function resolveRelativeUrl(value, baseUrl) {
  if (!baseUrl || value.startsWith('#') || value.startsWith('?') || value.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(value)) {
    return value;
  }
  try {
    return new URL(value, baseUrl).toString();
  } catch {
    return value;
  }
}

/** True when an image URL stays inside one of the listing's exact commit-pinned repository roots. */
function isAllowedUntrustedImageUrl(value, allowedRootUrls) {
  if (!allowedRootUrls?.length) {
    return false;
  }
  return allowedRootUrls.some(rootValue => {
    try {
      const root = new URL(rootValue);
      const image = new URL(value, root);
      const rootPath = root.pathname.endsWith('/') ? root.pathname : `${root.pathname}/`;
      return root.protocol === 'https:' && image.protocol === 'https:' && image.origin === root.origin && image.pathname.startsWith(rootPath);
    } catch {
      return false;
    }
  });
}
function copyAllowedAttributes(source, target, tag, options) {
  const allowedAttributes = ALLOWED_ATTRIBUTES[tag] ?? [];
  const policy = options.policy ?? 'untrusted';
  for (const attr of Array.from(source.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on')) {
      continue;
    }
    let value = attr.value;
    if (name === 'class' && policy === 'trustedCms') {
      value = sanitizeTrustedCmsClasses(value, tag);
      if (!value) {
        continue;
      }
    } else if (name === 'style' && policy === 'trustedCms') {
      value = sanitizeTrustedCmsStyle(value);
      if (!value) {
        continue;
      }
    } else if (!allowedAttributes.includes(name)) {
      continue;
    } else if (name === 'href') {
      value = resolveRelativeUrl(value, options.linkBaseUrl);
    } else if (name === 'src') {
      value = resolveRelativeUrl(value, options.imageBaseUrl);
    }
    if ((name === 'href' || name === 'src') && !isSafeUrl(value, policy)) {
      continue;
    }
    if (name === 'src' && policy === 'untrusted' && !isAllowedUntrustedImageUrl(value, options.allowedImageRootUrls)) {
      continue;
    }
    target.setAttribute(name, value);
  }

  // A link that opens in a new tab must not hand that tab a `window.opener` reference to us, and an
  // author-supplied `rel` never made it through the allowlist, so synthesize it. Any other target (a
  // named frame, `_parent`, `_top`) is dropped: nothing in SKILL.md needs to retarget our own frames.
  if (tag === 'a' && target.hasAttribute('target')) {
    if (target.getAttribute('target')?.toLowerCase() === '_blank') {
      target.setAttribute('rel', 'noopener noreferrer');
    } else {
      target.removeAttribute('target');
    }
  }

  // Every image needs a text alternative. An omitted alt is not equivalent to alt="": the
  // latter explicitly marks a decorative image and prevents assistive tech from reading its URL.
  if (tag === 'img' && !target.hasAttribute('alt')) {
    target.setAttribute('alt', '');
  }
}

/**
 * The sanitized equivalent of `node`, as a list of nodes to splice into the output in its place.
 *
 * A list rather than a single node because an element that is not on the tag allowlist is *unwrapped*
 * — it contributes its sanitized children and nothing else — which is what the old regex effectively
 * did when it deleted tag markers but left their content. Returning [] drops the node outright.
 */
function sanitizeNode(node, doc, options) {
  if (node.nodeType === Node.TEXT_NODE) {
    // Text is safe by construction: the serializer escapes it on the way out.
    return [doc.createTextNode(node.nodeValue ?? '')];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    // Comments and everything else (CDATA, processing instructions) carry no rendered content and
    // are a known mXSS aid, so they are dropped.
    return [];
  }
  const el = node;
  const tag = el.tagName.toLowerCase();
  if (FORBIDDEN_SUBTREE_TAGS.has(tag)) {
    return [];
  }
  if (!ALLOWED_TAGS.has(tag)) {
    return sanitizeChildren(el, doc, options);
  }

  // Built fresh, then populated: nothing from the parsed element carries over implicitly.
  const clean = doc.createElement(tag);
  copyAllowedAttributes(el, clean, tag, options);
  // A rejected image source must not leave a broken-image glyph or initiate any browser request.
  if (tag === 'img' && !clean.hasAttribute('src')) {
    return [];
  }
  for (const child of sanitizeChildren(el, doc, options)) {
    clean.appendChild(child);
  }

  // If a blocked image was the link's only content, remove the now-empty, unnamed control too.
  if (tag === 'a' && !clean.textContent?.trim() && !clean.hasChildNodes()) {
    return [];
  }

  // An image-only link has no accessible name when the image is explicitly decorative
  // (`alt=""`). Authored aria-labels remain disallowed because they could contradict visible link
  // text; this narrowly synthesized label is derived only from already-sanitized content.
  if (tag === 'a' && !(clean.textContent ?? '').trim()) {
    const children = Array.from(clean.children);
    const onlyImage = children.length === 1 && children[0].tagName.toLowerCase() === 'img' ? children[0] : null;
    if (onlyImage && !(onlyImage.getAttribute('alt') ?? '').trim()) {
      const accessibleName = (onlyImage.getAttribute('title') ?? '').trim() || (clean.getAttribute('href') ?? '').trim();
      if (accessibleName) {
        clean.setAttribute('aria-label', accessibleName);
      }
    }
  }
  return [clean];
}
function sanitizeChildren(parent, doc, options) {
  const result = [];
  for (const child of Array.from(parent.childNodes)) {
    result.push(...sanitizeNode(child, doc, options));
  }
  return result;
}
export function sanitizeHtml(html, options = {}) {
  // Parse into template content rather than a browsing document. Template descendants are inert and
  // cannot fetch an authored img src before the repository policy has inspected and removed it.
  const source = document.createElement('template');
  // eslint-disable-next-line @lwc/lwc/no-inner-html
  source.innerHTML = html;
  const clean = document.createElement('div');
  for (const child of sanitizeChildren(source.content, document, options)) {
    clean.appendChild(child);
  }

  // Reading (not assigning) innerHTML, on a detached element, to serialize the rebuilt tree.
  // eslint-disable-next-line @lwc/lwc/no-inner-html
  return clean.innerHTML;
}
export function sanitizeTrustedCmsHtml(html) {
  return sanitizeHtml(html, {
    policy: 'trustedCms'
  });
}
class Markdown extends LightningElement {
  constructor(...args) {
    super(...args);
    this._value = '';
    this._linkBaseUrl = '';
    this._imageBaseUrl = '';
    this._allowedImageRootUrls = [];
    this.cachedInputKey = '';
    this.cachedSanitizedHtml = '';
    this.renderedHtml = '';
    // Opt-in only: package-file viewers use this to report sanitized rendered links. Other markdown
    // consumers retain their current behavior, and the sanitizer remains the sole URL trust boundary.
    this.instrumentLinks = false;
  }
  handleContainerClick(event) {
    if (!this.instrumentLinks) {
      return;
    }
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) {
      return;
    }
    const image = anchor.querySelector('img');
    this.dispatchEvent(new CustomEvent('markdownlinkclick', {
      detail: {
        text: anchor.textContent?.trim() || anchor.getAttribute('aria-label')?.trim() || image?.getAttribute('alt')?.trim() || image?.getAttribute('title')?.trim() || anchor.href,
        url: anchor.href
      }
    }));
  }
  get value() {
    return this._value;
  }
  set value(v) {
    this._value = v ?? '';
    this.updateContent();
  }
  get linkBaseUrl() {
    return this._linkBaseUrl;
  }
  set linkBaseUrl(value) {
    this._linkBaseUrl = value ?? '';
    this.updateContent();
  }
  get imageBaseUrl() {
    return this._imageBaseUrl;
  }
  set imageBaseUrl(value) {
    this._imageBaseUrl = value ?? '';
    this.updateContent();
  }
  get allowedImageRootUrls() {
    return this._allowedImageRootUrls;
  }
  set allowedImageRootUrls(value) {
    this._allowedImageRootUrls = Array.isArray(value) ? [...value] : [];
    this.updateContent();
  }
  get sanitizedHtml() {
    const cacheKey = `${this._linkBaseUrl}\u0000${this._imageBaseUrl}\u0000${this._allowedImageRootUrls.join('\u0000')}\u0000${this._value}`;
    if (cacheKey === this.cachedInputKey) {
      return this.cachedSanitizedHtml;
    }
    this.cachedInputKey = cacheKey;
    // marked v18's parse() overloads return `Promise<string>` when `async: true`; passing `async: false`
    // narrows the overload to a plain `string`, which is required here since renderedCallback() sets
    // innerHTML synchronously. gfm defaults to true, so fenced code blocks and tables render as
    // <pre><code> / <table> without extra options.
    const html = marked.parse(this._value, {
      async: false
    });
    this.cachedSanitizedHtml = sanitizeHtml(html, {
      linkBaseUrl: this._linkBaseUrl,
      imageBaseUrl: this._imageBaseUrl,
      allowedImageRootUrls: this._allowedImageRootUrls
    });
    return this.cachedSanitizedHtml;
  }
  updateContent() {
    const containerEl = this.template?.querySelector('.container');
    const sanitizedHtml = this.sanitizedHtml;
    if (containerEl && sanitizedHtml !== this.renderedHtml) {
      containerEl.innerHTML = sanitizedHtml;
      this.renderedHtml = sanitizedHtml;
      for (const [selector, typeStyle] of Object.entries(HEADING_TYPE_STYLES)) {
        containerEl.querySelectorAll(selector).forEach(heading => {
          heading.setAttribute('type-style', typeStyle);
        });
      }
      // Fenced code blocks scroll horizontally through the <pre>. Make that scroll container
      // keyboard focusable so users who cannot use a pointer can reach clipped content.
      containerEl.querySelectorAll('pre').forEach(pre => pre.setAttribute('tabindex', '0'));
    }
  }
  renderedCallback() {
    this.updateContent();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Markdown, {
  publicProps: {
    instrumentLinks: {
      config: 0
    },
    value: {
      config: 3
    },
    linkBaseUrl: {
      config: 3
    },
    imageBaseUrl: {
      config: 3
    },
    allowedImageRootUrls: {
      config: 3
    }
  },
  fields: ["_value", "_linkBaseUrl", "_imageBaseUrl", "_allowedImageRootUrls", "cachedInputKey", "cachedSanitizedHtml", "renderedHtml"]
});
const __lwc_component_class_internal = _registerComponent(Markdown, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;